import React, { FC, useMemo } from 'react'
import { isBefore, parse } from '@e3dc-react/shell/Libs/date'

import { TableComplete, Header } from '@e3dc-react/shell/Elements/Table'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import CollapsedPaper from './CollapsedPaper'
import { useFullBridgeConvertersBySerialNumber } from '../../hooks/use-full-bridge-converters-by-serial-number'
import { FullBridgeConverter as FullBridgeConverterModel } from '../../models'

interface FullBridgeConverterProps {
  storageId?: string
}

/**
 * Full bridge converter element of the components page
 */
const FullBridgeConverter: FC<FullBridgeConverterProps> = ({ storageId }) => {
  const { t, selectedAppLanguage } = useCustomTranslation()
  const [convertersData, isLoadingConvertersData] = useFullBridgeConvertersBySerialNumber(storageId)

  const tableHeaders: Header<FullBridgeConverterModel>[] = useMemo(() => {
    return [
      {
        label: t('system-details.components.fbc.id'),
        value: 'moduleName',
        isSortable: true,
        sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      },
      {
        label: t('system-details.components.fbc.type'),
        value: 'type',
        isSortable: true,
        sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      },
      {
        label: t('system-details.components.fbc.assembly-number'),
        value: 'deviceSerial',
        isSortable: true,
        sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      },
      {
        label: t('system-details.components.fbc.revision'),
        value: 'cortexRevision',
        isSortable: true,
        sortFunction: nullsLast((a: number, b: number) => a - b),
      },
      {
        label: t('system-details.components.fbc.soft-version'),
        value: 'swVersion',
        isSortable: true,
        sortFunction: nullsLast((a: number, b: number) => a - b),
      },
      {
        label: t('system-details.components.fbc.date'),
        value: 'buildDate',
        isSortable: true,
        sortFunction: nullsLast((a: string, b: string) => {
          const dateA = parse(a, t('common.format.date-fns-date'), new Date())
          const dateB = parse(b, t('common.format.date-fns-date'), new Date())
          return isBefore(dateA, dateB) ? -1 : 1
        }),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppLanguage])

  return (
    <CollapsedPaper title={t('system-details.components.fbc.title')}>
      {isLoadingConvertersData ? (
        <EditorSkeleton />
      ) : (
        <TableComplete
          data={convertersData || []}
          header={tableHeaders}
          headerElevation={0}
          bodyElevation={0}
          footerElevation={0}
          coloredColumn
          disablePagination
        />
      )}
    </CollapsedPaper>
  )
}

export default FullBridgeConverter
